// TypeClassHider.tsx
import React, { useMemo, useCallback } from "react";
import { Chip, Group, Stack, Title, Button, ScrollArea, Tooltip, Badge } from "@mantine/core";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectDebugHiddenTypeClasses,
  setHiddenTypeClassesInRedux,
  clearHiddenTypeClassesInRedux,
} from "../DebugSlice";
import { selectDebugUniqueTypeClassesOnActiveBuildingStory } from "./twoDFloorplanSelectors";

const TypeClassHider: React.FC = () => {
  const dispatch = useAppDispatch();
  const allTypeClasses = useAppSelector(selectDebugUniqueTypeClassesOnActiveBuildingStory);
  const hidden = useAppSelector(selectDebugHiddenTypeClasses);

  const all = useMemo(() => allTypeClasses.map((s) => s.trim()).filter(Boolean), [allTypeClasses]);
  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  // ✔️ Viktigt: korrekt signatur för multiple = true
  const handleChange = useCallback(
    (values: string[]) => {
      const next = (values ?? []).map((s) => s.trim()).filter(Boolean);
      dispatch(setHiddenTypeClassesInRedux(next));
    },
    [dispatch]
  );

  const hideAll = useCallback(() => {
    dispatch(setHiddenTypeClassesInRedux(all));
  }, [dispatch, all]);

  const showAll = useCallback(() => {
    dispatch(clearHiddenTypeClassesInRedux());
  }, [dispatch]);

  return (
    <Stack gap="xs">
      <Group justify="space-between" wrap="nowrap">
        <Group gap={8}>
          <Title order={5} style={{ lineHeight: 1.2 }}>
            IFC type classes
          </Title>
          <Badge variant="light" size="sm">
            Hidden: {hidden.length}/{all.length}
          </Badge>
        </Group>

        <Group gap="xs">
          <Button size="xs" variant="subtle" onClick={showAll} disabled={hidden.length === 0}>
            Show all
          </Button>
          <Button
            size="xs"
            variant="subtle"
            onClick={hideAll}
            disabled={all.length === hidden.length}
          >
            Hide all
          </Button>
        </Group>
      </Group>

      <ScrollArea style={{ maxHeight: 220 }}>
        <Chip.Group multiple value={hidden} onChange={handleChange}>
          <Group gap="xs">
            {all.map((tc) => {
              const isHidden = hiddenSet.has(tc);
              return (
                <Tooltip
                  key={tc}
                  label={isHidden ? "Hidden (click to show)" : "Visible (click to hide)"}
                  withArrow
                  position="top"
                >
                  <Chip
                    value={tc}
                    size="xs"
                    variant="outline"
                    styles={{
                      label: {
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        textDecoration: isHidden ? "line-through" : "none",
                        opacity: isHidden ? 0.6 : 1,
                      },
                    }}
                  >
                    {isHidden ? <FiEyeOff /> : <FiEye />}
                    {tc}
                  </Chip>
                </Tooltip>
              );
            })}
          </Group>
        </Chip.Group>
      </ScrollArea>
    </Stack>
  );
};

export default TypeClassHider;
